export interface ZammadTicketStats {
  totalTickets: number;
  openTickets: number;
  inProgressTickets: number;
  resolvedTickets: number;
  closedTickets: number;
  slaCompliance: number;
  avgResolutionHours: number;
  avgFirstResponseMinutes: number;
  satisfactionAvg: number;
  ticketsByCategory: { name: string; count: number; color: string }[];
  ticketsByPriority: { name: string; count: number; color: string }[];
  monthlyTrend: { month: string; created: number; resolved: number }[];
  commonWords?: string[];
}

// ─── System Info ───
export interface SystemInfo {
  id: string;
  name: string;
  shortName: string;
  icon: string;
}

// ═══════════════════════════════════════════════════════════
// GLOBAL STATS
// ═══════════════════════════════════════════════════════════

export const mockZammadStats: ZammadTicketStats = {
  totalTickets: 1427,
  openTickets: 38,
  inProgressTickets: 52,
  resolvedTickets: 289,
  closedTickets: 1048,
  slaCompliance: 91.4,
  avgResolutionHours: 6.2,
  avgFirstResponseMinutes: 14,
  satisfactionAvg: 4.3,
  ticketsByCategory: [
    { name: "Hardware", count: 312, color: "bg-accent-500" },
    { name: "Software", count: 278, color: "bg-primary-500" },
    { name: "ERP NetSuite", count: 245, color: "bg-amber-500" },
    { name: "Redes", count: 198, color: "bg-emerald-500" },
    { name: "Accesos", count: 156, color: "bg-secondary-500" },
    { name: "Capacitación", count: 89, color: "bg-rose-400" },
    { name: "Otros", count: 149, color: "bg-foreground-400" },
  ],
  ticketsByPriority: [
    { name: "Crítica", count: 42, color: "bg-red-500" },
    { name: "Alta", count: 385, color: "bg-amber-500" },
    { name: "Media", count: 712, color: "bg-accent-500" },
    { name: "Baja", count: 288, color: "bg-foreground-400" },
  ],
  monthlyTrend: [
    { month: "Feb", created: 112, resolved: 108 },
    { month: "Mar", created: 145, resolved: 139 },
    { month: "Abr", created: 128, resolved: 131 },
    { month: "May", created: 156, resolved: 148 },
    { month: "Jun", created: 189, resolved: 182 },
    { month: "Jul", created: 84, resolved: 71 },
  ],
};

// ═══════════════════════════════════════════════════════════
// DEPARTMENT STATS
// ═══════════════════════════════════════════════════════════

export const mockDepartmentStats: Record<string, ZammadTicketStats> = {
  ti: {
    totalTickets: 847,
    openTickets: 22,
    inProgressTickets: 31,
    resolvedTickets: 168,
    closedTickets: 626,
    slaCompliance: 93.2,
    avgResolutionHours: 5.1,
    avgFirstResponseMinutes: 11,
    satisfactionAvg: 4.5,
    ticketsByCategory: [
      { name: "Hardware", count: 198, color: "bg-accent-500" },
      { name: "Software", count: 176, color: "bg-primary-500" },
      { name: "ERP NetSuite", count: 155, color: "bg-amber-500" },
      { name: "Redes", count: 132, color: "bg-emerald-500" },
      { name: "Accesos", count: 98, color: "bg-secondary-500" },
      { name: "Capacitación", count: 45, color: "bg-rose-400" },
      { name: "Otros", count: 43, color: "bg-foreground-400" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 18, color: "bg-red-500" },
      { name: "Alta", count: 210, color: "bg-amber-500" },
      { name: "Media", count: 442, color: "bg-accent-500" },
      { name: "Baja", count: 177, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 72, resolved: 69 },
      { month: "Mar", created: 88, resolved: 85 },
      { month: "Abr", created: 81, resolved: 79 },
      { month: "May", created: 95, resolved: 91 },
      { month: "Jun", created: 112, resolved: 108 },
      { month: "Jul", created: 48, resolved: 42 },
    ],
  },
  administracion: {
    totalTickets: 128,
    openTickets: 5,
    inProgressTickets: 7,
    resolvedTickets: 34,
    closedTickets: 82,
    slaCompliance: 94.8,
    avgResolutionHours: 3.8,
    avgFirstResponseMinutes: 9,
    satisfactionAvg: 4.6,
    ticketsByCategory: [
      { name: "Flotilla", count: 32, color: "bg-accent-500" },
      { name: "Viáticos", count: 28, color: "bg-primary-500" },
      { name: "Vuelos", count: 22, color: "bg-amber-500" },
      { name: "Hospedaje", count: 18, color: "bg-emerald-500" },
      { name: "Proveedores", count: 16, color: "bg-secondary-500" },
      { name: "Estacionamiento", count: 12, color: "bg-rose-400" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 2, color: "bg-red-500" },
      { name: "Alta", count: 31, color: "bg-amber-500" },
      { name: "Media", count: 68, color: "bg-accent-500" },
      { name: "Baja", count: 27, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 12, resolved: 11 },
      { month: "Mar", created: 15, resolved: 14 },
      { month: "Abr", created: 11, resolved: 12 },
      { month: "May", created: 14, resolved: 13 },
      { month: "Jun", created: 18, resolved: 17 },
      { month: "Jul", created: 8, resolved: 6 },
    ],
  },
  "bid-tecnologia": {
    totalTickets: 95,
    openTickets: 3,
    inProgressTickets: 4,
    resolvedTickets: 22,
    closedTickets: 66,
    slaCompliance: 96.1,
    avgResolutionHours: 4.2,
    avgFirstResponseMinutes: 8,
    satisfactionAvg: 4.7,
    ticketsByCategory: [
      { name: "Nomisystem", count: 28, color: "bg-accent-500" },
      { name: "SharePoint", count: 24, color: "bg-primary-500" },
      { name: "SILIC", count: 18, color: "bg-amber-500" },
      { name: "SEIEM Nómina", count: 15, color: "bg-emerald-500" },
      { name: "SEIEM Infra", count: 10, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 1, color: "bg-red-500" },
      { name: "Alta", count: 22, color: "bg-amber-500" },
      { name: "Media", count: 51, color: "bg-accent-500" },
      { name: "Baja", count: 21, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 9, resolved: 8 },
      { month: "Mar", created: 11, resolved: 11 },
      { month: "Abr", created: 8, resolved: 9 },
      { month: "May", created: 10, resolved: 9 },
      { month: "Jun", created: 13, resolved: 12 },
      { month: "Jul", created: 6, resolved: 5 },
    ],
  },
  contraloria: {
    totalTickets: 72,
    openTickets: 2,
    inProgressTickets: 3,
    resolvedTickets: 18,
    closedTickets: 49,
    slaCompliance: 97.3,
    avgResolutionHours: 3.2,
    avgFirstResponseMinutes: 7,
    satisfactionAvg: 4.8,
    ticketsByCategory: [
      { name: "CxC", count: 24, color: "bg-accent-500" },
      { name: "CxP", count: 20, color: "bg-primary-500" },
      { name: "Fiscal", count: 18, color: "bg-amber-500" },
      { name: "Auditoría", count: 10, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 1, color: "bg-red-500" },
      { name: "Alta", count: 16, color: "bg-amber-500" },
      { name: "Media", count: 38, color: "bg-accent-500" },
      { name: "Baja", count: 17, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 7, resolved: 7 },
      { month: "Mar", created: 8, resolved: 7 },
      { month: "Abr", created: 6, resolved: 6 },
      { month: "May", created: 9, resolved: 8 },
      { month: "Jun", created: 10, resolved: 10 },
      { month: "Jul", created: 4, resolved: 3 },
    ],
  },
  "diseno-marketing": {
    totalTickets: 62,
    openTickets: 1,
    inProgressTickets: 2,
    resolvedTickets: 15,
    closedTickets: 44,
    slaCompliance: 95.6,
    avgResolutionHours: 2.8,
    avgFirstResponseMinutes: 6,
    satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Medios Digitales", count: 34, color: "bg-accent-500" },
      { name: "Medios Impresos", count: 20, color: "bg-primary-500" },
      { name: "Audiovisual", count: 8, color: "bg-amber-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 10, color: "bg-amber-500" },
      { name: "Media", count: 36, color: "bg-accent-500" },
      { name: "Baja", count: 16, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 6, resolved: 6 },
      { month: "Mar", created: 7, resolved: 6 },
      { month: "Abr", created: 5, resolved: 5 },
      { month: "May", created: 8, resolved: 8 },
      { month: "Jun", created: 9, resolved: 8 },
      { month: "Jul", created: 3, resolved: 3 },
    ],
  },
  "servicios-generales": {
    totalTickets: 110,
    openTickets: 4,
    inProgressTickets: 3,
    resolvedTickets: 28,
    closedTickets: 75,
    slaCompliance: 92.5,
    avgResolutionHours: 4.5,
    avgFirstResponseMinutes: 12,
    satisfactionAvg: 4.4,
    ticketsByCategory: [
      { name: "Mantenimiento", count: 38, color: "bg-accent-500" },
      { name: "Mensajería", count: 25, color: "bg-primary-500" },
      { name: "Papelería", count: 20, color: "bg-amber-500" },
      { name: "Acceso", count: 17, color: "bg-emerald-500" },
      { name: "Proveedores", count: 10, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 3, color: "bg-red-500" },
      { name: "Alta", count: 28, color: "bg-amber-500" },
      { name: "Media", count: 55, color: "bg-accent-500" },
      { name: "Baja", count: 24, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 9, resolved: 9 },
      { month: "Mar", created: 12, resolved: 11 },
      { month: "Abr", created: 10, resolved: 10 },
      { month: "May", created: 13, resolved: 12 },
      { month: "Jun", created: 16, resolved: 15 },
      { month: "Jul", created: 7, resolved: 6 },
    ],
  },
  "talento-humano": {
    totalTickets: 85,
    openTickets: 1,
    inProgressTickets: 1,
    resolvedTickets: 20,
    closedTickets: 63,
    slaCompliance: 97.8,
    avgResolutionHours: 2.5,
    avgFirstResponseMinutes: 5,
    satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Nómina", count: 25, color: "bg-accent-500" },
      { name: "Reclutamiento", count: 20, color: "bg-primary-500" },
      { name: "Capacitación", count: 16, color: "bg-amber-500" },
      { name: "Beneficios", count: 14, color: "bg-emerald-500" },
      { name: "Expedientes", count: 10, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 14, color: "bg-amber-500" },
      { name: "Media", count: 48, color: "bg-accent-500" },
      { name: "Baja", count: 23, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 8, resolved: 7 },
      { month: "Mar", created: 9, resolved: 9 },
      { month: "Abr", created: 7, resolved: 7 },
      { month: "May", created: 10, resolved: 10 },
      { month: "Jun", created: 11, resolved: 10 },
      { month: "Jul", created: 5, resolved: 4 },
    ],
  },
  legal: {
    totalTickets: 28,
    openTickets: 0,
    inProgressTickets: 1,
    resolvedTickets: 7,
    closedTickets: 20,
    slaCompliance: 98.5,
    avgResolutionHours: 2.1,
    avgFirstResponseMinutes: 4,
    satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Contratos", count: 10, color: "bg-accent-500" },
      { name: "Consultoría", count: 8, color: "bg-primary-500" },
      { name: "Gobierno Corp.", count: 5, color: "bg-amber-500" },
      { name: "Prop. Intelectual", count: 3, color: "bg-emerald-500" },
      { name: "Litigio", count: 2, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 5, color: "bg-amber-500" },
      { name: "Media", count: 16, color: "bg-accent-500" },
      { name: "Baja", count: 7, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 3, resolved: 3 },
      { month: "Mar", created: 4, resolved: 3 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 4, resolved: 4 },
      { month: "Jun", created: 5, resolved: 5 },
      { month: "Jul", created: 2, resolved: 1 },
    ],
  },
};

// ═══════════════════════════════════════════════════════════
// SYSTEM LISTS PER DEPARTMENT
// ═══════════════════════════════════════════════════════════

export const tiSystems: SystemInfo[] = [
  { id: "mesa-de-ayuda", name: "Mesa de Ayuda", shortName: "MA", icon: "ri-customer-service-2-line" },
  { id: "crm-hubspot", name: "CRM (HubSpot)", shortName: "CRM", icon: "ri-bar-chart-box-line" },
  { id: "erp-netsuite", name: "ERP (NetSuite)", shortName: "ERP", icon: "ri-database-2-line" },
  { id: "data-lake", name: "Data Lake", shortName: "DL", icon: "ri-stack-line" },
  { id: "bpm", name: "BPM", shortName: "BPM", icon: "ri-flow-chart" },
  { id: "solicitudes-sitio", name: "Solicitudes en Sitio", shortName: "SIT", icon: "ri-building-2-line" },
  { id: "talento-humano-ti", name: "Sist. Talento Humano", shortName: "TH", icon: "ri-team-line" },
];

export const administracionSystems: SystemInfo[] = [
  { id: "adm-flotilla", name: "Flotilla Vehicular", shortName: "FLT", icon: "ri-car-line" },
  { id: "adm-viaticos", name: "Viáticos y Reembolsos", shortName: "VIA", icon: "ri-file-list-3-line" },
  { id: "adm-vuelos", name: "Vuelos y Hospedaje", shortName: "VUE", icon: "ri-flight-takeoff-line" },
  { id: "adm-proveedores", name: "Proveedores", shortName: "PRO", icon: "ri-exchange-line" },
  { id: "adm-estacionamiento", name: "Estacionamiento", shortName: "EST", icon: "ri-parking-box-line" },
];

export const bidTecnologiaSystems: SystemInfo[] = [
  { id: "bid-nomisystem", name: "Nomisystem", shortName: "NOM", icon: "ri-user-settings-line" },
  { id: "bid-sharepoint", name: "SharePoint", shortName: "SP", icon: "ri-share-box-line" },
  { id: "bid-silic", name: "SILIC", shortName: "SLC", icon: "ri-award-line" },
  { id: "bid-seiem-nomina", name: "SEIEM Nómina", shortName: "SNM", icon: "ri-money-dollar-circle-line" },
  { id: "bid-seiem-infra", name: "SEIEM Infraestructura", shortName: "SIF", icon: "ri-building-2-line" },
];

export const contraloriaSystems: SystemInfo[] = [
  { id: "ctr-cxc", name: "Cuentas por Cobrar", shortName: "CxC", icon: "ri-arrow-up-circle-line" },
  { id: "ctr-cxp", name: "Cuentas por Pagar", shortName: "CxP", icon: "ri-arrow-down-circle-line" },
  { id: "ctr-fiscal", name: "Fiscal", shortName: "FIS", icon: "ri-government-line" },
  { id: "ctr-auditoria", name: "Auditoría", shortName: "AUD", icon: "ri-check-double-line" },
];

export const disenoMarketingSystems: SystemInfo[] = [
  { id: "dm-digitales", name: "Medios Digitales", shortName: "DIG", icon: "ri-computer-line" },
  { id: "dm-impresos", name: "Medios Impresos", shortName: "IMP", icon: "ri-printer-line" },
  { id: "dm-audiovisual", name: "Audiovisual", shortName: "AV", icon: "ri-vidicon-line" },
];

export const serviciosGeneralesSystems: SystemInfo[] = [
  { id: "sg-mantenimiento", name: "Mantenimiento", shortName: "MTO", icon: "ri-settings-3-line" },
  { id: "sg-mensajeria", name: "Mensajería", shortName: "MSJ", icon: "ri-mail-send-line" },
  { id: "sg-papeleria", name: "Papelería", shortName: "PAP", icon: "ri-file-copy-line" },
  { id: "sg-acceso", name: "Acceso y Seguridad", shortName: "ACC", icon: "ri-shield-line" },
  { id: "sg-proveedores", name: "Proveedores Montacargas", shortName: "PMK", icon: "ri-truck-line" },
];

export const talentoHumanoSystems: SystemInfo[] = [
  { id: "th-reclutamiento", name: "Reclutamiento", shortName: "REC", icon: "ri-user-search-line" },
  { id: "th-nomina", name: "Nómina", shortName: "NOM", icon: "ri-money-dollar-circle-line" },
  { id: "th-capacitacion", name: "Capacitación", shortName: "CAP", icon: "ri-graduation-cap-line" },
  { id: "th-clima", name: "Clima Laboral", shortName: "CLI", icon: "ri-emotion-happy-line" },
  { id: "th-beneficios", name: "Beneficios", shortName: "BEN", icon: "ri-heart-2-line" },
  { id: "th-expedientes", name: "Expedientes", shortName: "EXP", icon: "ri-folder-user-line" },
];

export const legalSystems: SystemInfo[] = [
  { id: "leg-contratos", name: "Contratos", shortName: "CTR", icon: "ri-file-text-line" },
  { id: "leg-consultoria", name: "Consultoría", shortName: "CNS", icon: "ri-chat-check-line" },
  { id: "leg-gobierno", name: "Gobierno Corporativo", shortName: "GOB", icon: "ri-building-line" },
  { id: "leg-litigio", name: "Litigio", shortName: "LIT", icon: "ri-scales-3-line" },
  { id: "leg-propiedad", name: "Propiedad Intelectual", shortName: "PI", icon: "ri-lightbulb-flash-line" },
  { id: "leg-cumplimiento", name: "Cumplimiento Normativo", shortName: "CMP", icon: "ri-shield-check-line" },
];

// ─── Master lookup: dept ID → system list ───
export const systemsByDeptId: Record<string, SystemInfo[]> = {
  ti: tiSystems,
  administracion: administracionSystems,
  "bid-tecnologia": bidTecnologiaSystems,
  contraloria: contraloriaSystems,
  "diseno-marketing": disenoMarketingSystems,
  "servicios-generales": serviciosGeneralesSystems,
  "talento-humano": talentoHumanoSystems,
  legal: legalSystems,
};

// ═══════════════════════════════════════════════════════════
// SYSTEM STATS — TI
// ═══════════════════════════════════════════════════════════

export const mockTISystemStats: Record<string, ZammadTicketStats> = {
  "mesa-de-ayuda": {
    totalTickets: 245,
    openTickets: 11,
    inProgressTickets: 14,
    resolvedTickets: 52,
    closedTickets: 168,
    slaCompliance: 91.2,
    avgResolutionHours: 5.8,
    avgFirstResponseMinutes: 16,
    satisfactionAvg: 4.2,
    ticketsByCategory: [
      { name: "Soporte Técnico", count: 68, color: "bg-accent-500" },
      { name: "Accesos y Cuentas", count: 52, color: "bg-primary-500" },
      { name: "Hardware", count: 45, color: "bg-amber-500" },
      { name: "Correo y Comunicación", count: 38, color: "bg-emerald-500" },
      { name: "Software", count: 42, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 7, color: "bg-red-500" },
      { name: "Alta", count: 78, color: "bg-amber-500" },
      { name: "Media", count: 112, color: "bg-accent-500" },
      { name: "Baja", count: 48, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 24, resolved: 22 },
      { month: "Mar", created: 28, resolved: 26 },
      { month: "Abr", created: 26, resolved: 25 },
      { month: "May", created: 31, resolved: 29 },
      { month: "Jun", created: 35, resolved: 33 },
      { month: "Jul", created: 15, resolved: 13 },
    ],
  },
  "crm-hubspot": {
    totalTickets: 112,
    openTickets: 3,
    inProgressTickets: 5,
    resolvedTickets: 24,
    closedTickets: 80,
    slaCompliance: 94.1,
    avgResolutionHours: 4.2,
    avgFirstResponseMinutes: 10,
    satisfactionAvg: 4.5,
    ticketsByCategory: [
      { name: "Contactos y Empresas", count: 28, color: "bg-accent-500" },
      { name: "Ventas y Pipeline", count: 32, color: "bg-primary-500" },
      { name: "Marketing", count: 22, color: "bg-amber-500" },
      { name: "Integraciones", count: 18, color: "bg-emerald-500" },
      { name: "Accesos", count: 12, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 2, color: "bg-red-500" },
      { name: "Alta", count: 28, color: "bg-amber-500" },
      { name: "Media", count: 58, color: "bg-accent-500" },
      { name: "Baja", count: 24, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 11, resolved: 10 },
      { month: "Mar", created: 13, resolved: 12 },
      { month: "Abr", created: 10, resolved: 10 },
      { month: "May", created: 14, resolved: 13 },
      { month: "Jun", created: 16, resolved: 15 },
      { month: "Jul", created: 7, resolved: 6 },
    ],
  },
  "erp-netsuite": {
    totalTickets: 198,
    openTickets: 4,
    inProgressTickets: 6,
    resolvedTickets: 42,
    closedTickets: 146,
    slaCompliance: 92.5,
    avgResolutionHours: 5.5,
    avgFirstResponseMinutes: 13,
    satisfactionAvg: 4.3,
    ticketsByCategory: [
      { name: "Finanzas y Contabilidad", count: 52, color: "bg-accent-500" },
      { name: "CxP", count: 34, color: "bg-primary-500" },
      { name: "CxC", count: 38, color: "bg-amber-500" },
      { name: "Intercompañía", count: 18, color: "bg-emerald-500" },
      { name: "Compras e Inventarios", count: 24, color: "bg-secondary-500" },
      { name: "Impuestos", count: 16, color: "bg-rose-400" },
      { name: "Otros", count: 16, color: "bg-foreground-400" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 5, color: "bg-red-500" },
      { name: "Alta", count: 48, color: "bg-amber-500" },
      { name: "Media", count: 102, color: "bg-accent-500" },
      { name: "Baja", count: 43, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 20, resolved: 19 },
      { month: "Mar", created: 22, resolved: 21 },
      { month: "Abr", created: 19, resolved: 18 },
      { month: "May", created: 24, resolved: 23 },
      { month: "Jun", created: 28, resolved: 27 },
      { month: "Jul", created: 12, resolved: 11 },
    ],
  },
  "data-lake": {
    totalTickets: 68,
    openTickets: 1,
    inProgressTickets: 2,
    resolvedTickets: 15,
    closedTickets: 50,
    slaCompliance: 96.8,
    avgResolutionHours: 3.1,
    avgFirstResponseMinutes: 8,
    satisfactionAvg: 4.7,
    ticketsByCategory: [
      { name: "Acceso a Datos", count: 22, color: "bg-accent-500" },
      { name: "Reportes y Análisis", count: 26, color: "bg-primary-500" },
      { name: "Integraciones", count: 12, color: "bg-amber-500" },
      { name: "Calidad de Datos", count: 8, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 14, color: "bg-amber-500" },
      { name: "Media", count: 38, color: "bg-accent-500" },
      { name: "Baja", count: 16, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 7, resolved: 7 },
      { month: "Mar", created: 8, resolved: 8 },
      { month: "Abr", created: 6, resolved: 6 },
      { month: "May", created: 9, resolved: 8 },
      { month: "Jun", created: 10, resolved: 10 },
      { month: "Jul", created: 4, resolved: 4 },
    ],
  },
  bpm: {
    totalTickets: 45,
    openTickets: 1,
    inProgressTickets: 1,
    resolvedTickets: 10,
    closedTickets: 33,
    slaCompliance: 95.1,
    avgResolutionHours: 3.8,
    avgFirstResponseMinutes: 9,
    satisfactionAvg: 4.6,
    ticketsByCategory: [
      { name: "Procesos", count: 15, color: "bg-accent-500" },
      { name: "Automatización", count: 16, color: "bg-primary-500" },
      { name: "Indicadores", count: 8, color: "bg-amber-500" },
      { name: "Gobernanza", count: 6, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 8, color: "bg-amber-500" },
      { name: "Media", count: 25, color: "bg-accent-500" },
      { name: "Baja", count: 12, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 5, resolved: 4 },
      { month: "Mar", created: 6, resolved: 6 },
      { month: "Abr", created: 4, resolved: 4 },
      { month: "May", created: 5, resolved: 5 },
      { month: "Jun", created: 7, resolved: 6 },
      { month: "Jul", created: 3, resolved: 3 },
    ],
  },
  "solicitudes-sitio": {
    totalTickets: 98,
    openTickets: 2,
    inProgressTickets: 2,
    resolvedTickets: 22,
    closedTickets: 72,
    slaCompliance: 93.4,
    avgResolutionHours: 4.5,
    avgFirstResponseMinutes: 11,
    satisfactionAvg: 4.4,
    ticketsByCategory: [
      { name: "Equipos", count: 28, color: "bg-accent-500" },
      { name: "Impresión", count: 22, color: "bg-primary-500" },
      { name: "Espacios", count: 18, color: "bg-amber-500" },
      { name: "Acceso Físico", count: 16, color: "bg-emerald-500" },
      { name: "Conectividad", count: 14, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 2, color: "bg-red-500" },
      { name: "Alta", count: 22, color: "bg-amber-500" },
      { name: "Media", count: 52, color: "bg-accent-500" },
      { name: "Baja", count: 22, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 10, resolved: 9 },
      { month: "Mar", created: 11, resolved: 11 },
      { month: "Abr", created: 9, resolved: 9 },
      { month: "May", created: 13, resolved: 12 },
      { month: "Jun", created: 15, resolved: 14 },
      { month: "Jul", created: 6, resolved: 5 },
    ],
  },
  "talento-humano-ti": {
    totalTickets: 81,
    openTickets: 0,
    inProgressTickets: 1,
    resolvedTickets: 3,
    closedTickets: 77,
    slaCompliance: 97.3,
    avgResolutionHours: 3.5,
    avgFirstResponseMinutes: 7,
    satisfactionAvg: 4.8,
    ticketsByCategory: [
      { name: "Gestión de Talento", count: 12, color: "bg-accent-500" },
      { name: "Reclutamiento", count: 18, color: "bg-primary-500" },
      { name: "Admin. de Personal", count: 15, color: "bg-amber-500" },
      { name: "Onboarding", count: 10, color: "bg-emerald-500" },
      { name: "Tiempo y Asistencia", count: 14, color: "bg-secondary-500" },
      { name: "Otros", count: 12, color: "bg-foreground-400" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 10, color: "bg-amber-500" },
      { name: "Media", count: 45, color: "bg-accent-500" },
      { name: "Baja", count: 26, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 8, resolved: 8 },
      { month: "Mar", created: 9, resolved: 9 },
      { month: "Abr", created: 7, resolved: 7 },
      { month: "May", created: 10, resolved: 9 },
      { month: "Jun", created: 11, resolved: 11 },
      { month: "Jul", created: 5, resolved: 4 },
    ],
  },
};

// ═══════════════════════════════════════════════════════════
// SYSTEM STATS — ADMINISTRACIÓN
// ═══════════════════════════════════════════════════════════

export const mockAdministracionSystemStats: Record<string, ZammadTicketStats> = {
  "adm-flotilla": {
    totalTickets: 32, openTickets: 1, inProgressTickets: 2, resolvedTickets: 8, closedTickets: 21,
    slaCompliance: 93.4, avgResolutionHours: 3.2, avgFirstResponseMinutes: 8, satisfactionAvg: 4.5,
    ticketsByCategory: [
      { name: "Reservación", count: 12, color: "bg-accent-500" },
      { name: "Mantenimiento", count: 8, color: "bg-primary-500" },
      { name: "Incidencias", count: 7, color: "bg-amber-500" },
      { name: "Asignación", count: 5, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 7, color: "bg-amber-500" },
      { name: "Media", count: 18, color: "bg-accent-500" },
      { name: "Baja", count: 7, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 3, resolved: 3 },
      { month: "Mar", created: 4, resolved: 3 },
      { month: "Abr", created: 3, resolved: 3 },
      { month: "May", created: 4, resolved: 4 },
      { month: "Jun", created: 5, resolved: 5 },
      { month: "Jul", created: 2, resolved: 1 },
    ],
  },
  "adm-viaticos": {
    totalTickets: 28, openTickets: 0, inProgressTickets: 2, resolvedTickets: 8, closedTickets: 18,
    slaCompliance: 95.2, avgResolutionHours: 4.1, avgFirstResponseMinutes: 10, satisfactionAvg: 4.7,
    ticketsByCategory: [
      { name: "Solicitud Reembolso", count: 10, color: "bg-accent-500" },
      { name: "Autorización", count: 8, color: "bg-primary-500" },
      { name: "Comprobantes", count: 6, color: "bg-amber-500" },
      { name: "Cancelaciones", count: 4, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 6, color: "bg-amber-500" },
      { name: "Media", count: 15, color: "bg-accent-500" },
      { name: "Baja", count: 7, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 3, resolved: 3 },
      { month: "Mar", created: 4, resolved: 4 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 3, resolved: 3 },
      { month: "Jun", created: 4, resolved: 4 },
      { month: "Jul", created: 2, resolved: 1 },
    ],
  },
  "adm-vuelos": {
    totalTickets: 22, openTickets: 1, inProgressTickets: 1, resolvedTickets: 6, closedTickets: 14,
    slaCompliance: 94.8, avgResolutionHours: 3.5, avgFirstResponseMinutes: 9, satisfactionAvg: 4.6,
    ticketsByCategory: [
      { name: "Reserva Vuelos", count: 9, color: "bg-accent-500" },
      { name: "Hospedaje", count: 7, color: "bg-primary-500" },
      { name: "Cambios de Itinerario", count: 4, color: "bg-amber-500" },
      { name: "Cancelaciones", count: 2, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 1, color: "bg-red-500" },
      { name: "Alta", count: 7, color: "bg-amber-500" },
      { name: "Media", count: 10, color: "bg-accent-500" },
      { name: "Baja", count: 4, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 3, resolved: 3 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 3, resolved: 2 },
      { month: "Jun", created: 4, resolved: 3 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "adm-proveedores": {
    totalTickets: 16, openTickets: 0, inProgressTickets: 1, resolvedTickets: 5, closedTickets: 10,
    slaCompliance: 96.1, avgResolutionHours: 4.8, avgFirstResponseMinutes: 12, satisfactionAvg: 4.4,
    ticketsByCategory: [
      { name: "Alta Proveedor", count: 6, color: "bg-accent-500" },
      { name: "Baja Proveedor", count: 4, color: "bg-primary-500" },
      { name: "Cambios", count: 3, color: "bg-amber-500" },
      { name: "Validación", count: 3, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 5, color: "bg-amber-500" },
      { name: "Media", count: 8, color: "bg-accent-500" },
      { name: "Baja", count: 3, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 1 },
      { month: "Mar", created: 2, resolved: 2 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 2, resolved: 2 },
      { month: "Jun", created: 3, resolved: 3 },
      { month: "Jul", created: 1, resolved: 0 },
    ],
  },
  "adm-estacionamiento": {
    totalTickets: 30, openTickets: 3, inProgressTickets: 1, resolvedTickets: 7, closedTickets: 19,
    slaCompliance: 95.6, avgResolutionHours: 1.8, avgFirstResponseMinutes: 4, satisfactionAvg: 4.8,
    ticketsByCategory: [
      { name: "Reserva Diaria", count: 12, color: "bg-accent-500" },
      { name: "Asignación Fija", count: 8, color: "bg-primary-500" },
      { name: "Visitantes", count: 6, color: "bg-amber-500" },
      { name: "Incidencias", count: 4, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 1, color: "bg-red-500" },
      { name: "Alta", count: 6, color: "bg-amber-500" },
      { name: "Media", count: 17, color: "bg-accent-500" },
      { name: "Baja", count: 6, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 2, resolved: 2 },
      { month: "Abr", created: 3, resolved: 4 },
      { month: "May", created: 2, resolved: 2 },
      { month: "Jun", created: 2, resolved: 2 },
      { month: "Jul", created: 2, resolved: 1 },
    ],
  },
};

// ═══════════════════════════════════════════════════════════
// SYSTEM STATS — BID TECNOLOGÍA
// ═══════════════════════════════════════════════════════════

export const mockBidTecnologiaSystemStats: Record<string, ZammadTicketStats> = {
  "bid-nomisystem": {
    totalTickets: 28, openTickets: 1, inProgressTickets: 1, resolvedTickets: 7, closedTickets: 19,
    slaCompliance: 95.8, avgResolutionHours: 4.2, avgFirstResponseMinutes: 8, satisfactionAvg: 4.7,
    ticketsByCategory: [
      { name: "Plazas", count: 8, color: "bg-accent-500" },
      { name: "Nómina", count: 7, color: "bg-primary-500" },
      { name: "Reportes", count: 6, color: "bg-amber-500" },
      { name: "Expedientes", count: 4, color: "bg-emerald-500" },
      { name: "Responsivas", count: 3, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 7, color: "bg-amber-500" },
      { name: "Media", count: 15, color: "bg-accent-500" },
      { name: "Baja", count: 6, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 3, resolved: 3 },
      { month: "Mar", created: 3, resolved: 3 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 3, resolved: 3 },
      { month: "Jun", created: 4, resolved: 4 },
      { month: "Jul", created: 2, resolved: 1 },
    ],
  },
  "bid-sharepoint": {
    totalTickets: 24, openTickets: 0, inProgressTickets: 1, resolvedTickets: 6, closedTickets: 17,
    slaCompliance: 97.2, avgResolutionHours: 3.8, avgFirstResponseMinutes: 7, satisfactionAvg: 4.8,
    ticketsByCategory: [
      { name: "Accesos", count: 10, color: "bg-accent-500" },
      { name: "Carpetas", count: 8, color: "bg-primary-500" },
      { name: "Permisos", count: 4, color: "bg-amber-500" },
      { name: "Migración", count: 2, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 5, color: "bg-amber-500" },
      { name: "Media", count: 14, color: "bg-accent-500" },
      { name: "Baja", count: 5, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 3, resolved: 3 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 3, resolved: 2 },
      { month: "Jun", created: 3, resolved: 3 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "bid-silic": {
    totalTickets: 18, openTickets: 1, inProgressTickets: 1, resolvedTickets: 4, closedTickets: 12,
    slaCompliance: 95.5, avgResolutionHours: 5.2, avgFirstResponseMinutes: 10, satisfactionAvg: 4.5,
    ticketsByCategory: [
      { name: "Acceso SILIC", count: 6, color: "bg-accent-500" },
      { name: "Soporte Operativo", count: 5, color: "bg-primary-500" },
      { name: "Mejoras", count: 4, color: "bg-amber-500" },
      { name: "Errores", count: 3, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 4, color: "bg-amber-500" },
      { name: "Media", count: 10, color: "bg-accent-500" },
      { name: "Baja", count: 4, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 1 },
      { month: "Mar", created: 2, resolved: 2 },
      { month: "Abr", created: 1, resolved: 2 },
      { month: "May", created: 2, resolved: 1 },
      { month: "Jun", created: 3, resolved: 2 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "bid-seiem-nomina": {
    totalTickets: 15, openTickets: 0, inProgressTickets: 1, resolvedTickets: 3, closedTickets: 11,
    slaCompliance: 97.8, avgResolutionHours: 3.2, avgFirstResponseMinutes: 5, satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Expediente Electrónico", count: 6, color: "bg-accent-500" },
      { name: "Portal Servicios", count: 5, color: "bg-primary-500" },
      { name: "APP SEIEM", count: 4, color: "bg-amber-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 1, color: "bg-red-500" },
      { name: "Alta", count: 3, color: "bg-amber-500" },
      { name: "Media", count: 8, color: "bg-accent-500" },
      { name: "Baja", count: 3, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 2, resolved: 2 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 1, resolved: 1 },
      { month: "Jun", created: 2, resolved: 2 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "bid-seiem-infra": {
    totalTickets: 10, openTickets: 1, inProgressTickets: 0, resolvedTickets: 2, closedTickets: 7,
    slaCompliance: 94.2, avgResolutionHours: 4.5, avgFirstResponseMinutes: 12, satisfactionAvg: 4.3,
    ticketsByCategory: [
      { name: "Limpieza SITE", count: 3, color: "bg-accent-500" },
      { name: "Pintura", count: 2, color: "bg-primary-500" },
      { name: "Biométricos", count: 3, color: "bg-amber-500" },
      { name: "UPS y Aire", count: 2, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 3, color: "bg-amber-500" },
      { name: "Media", count: 4, color: "bg-accent-500" },
      { name: "Baja", count: 3, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 1, resolved: 1 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 1, resolved: 1 },
      { month: "Jun", created: 1, resolved: 1 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
};

// ═══════════════════════════════════════════════════════════
// SYSTEM STATS — CONTRALORÍA
// ═══════════════════════════════════════════════════════════

export const mockContraloriaSystemStats: Record<string, ZammadTicketStats> = {
  "ctr-cxc": {
    totalTickets: 24, openTickets: 1, inProgressTickets: 1, resolvedTickets: 6, closedTickets: 16,
    slaCompliance: 96.8, avgResolutionHours: 3.4, avgFirstResponseMinutes: 7, satisfactionAvg: 4.7,
    ticketsByCategory: [
      { name: "Cancelación Facturas", count: 8, color: "bg-accent-500" },
      { name: "Facturas de Venta", count: 6, color: "bg-primary-500" },
      { name: "Notas de Crédito", count: 5, color: "bg-amber-500" },
      { name: "Notas de Cargo", count: 3, color: "bg-emerald-500" },
      { name: "Cotización Equipo", count: 2, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 5, color: "bg-amber-500" },
      { name: "Media", count: 13, color: "bg-accent-500" },
      { name: "Baja", count: 6, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 3, resolved: 3 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 3, resolved: 2 },
      { month: "Jun", created: 4, resolved: 3 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "ctr-cxp": {
    totalTickets: 20, openTickets: 0, inProgressTickets: 1, resolvedTickets: 5, closedTickets: 14,
    slaCompliance: 97.5, avgResolutionHours: 2.8, avgFirstResponseMinutes: 6, satisfactionAvg: 4.8,
    ticketsByCategory: [
      { name: "Estados Financieros", count: 8, color: "bg-accent-500" },
      { name: "Razones Financieras", count: 6, color: "bg-primary-500" },
      { name: "Conciliaciones", count: 4, color: "bg-amber-500" },
      { name: "Reportes", count: 2, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 4, color: "bg-amber-500" },
      { name: "Media", count: 11, color: "bg-accent-500" },
      { name: "Baja", count: 5, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 2, resolved: 2 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 3, resolved: 3 },
      { month: "Jun", created: 2, resolved: 2 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "ctr-fiscal": {
    totalTickets: 18, openTickets: 1, inProgressTickets: 1, resolvedTickets: 4, closedTickets: 12,
    slaCompliance: 98.1, avgResolutionHours: 3.1, avgFirstResponseMinutes: 5, satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Constancias Fiscales", count: 7, color: "bg-accent-500" },
      { name: "Declaraciones", count: 5, color: "bg-primary-500" },
      { name: "Validación Facturas", count: 4, color: "bg-amber-500" },
      { name: "Opinión Cumplimiento", count: 2, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 3, color: "bg-amber-500" },
      { name: "Media", count: 10, color: "bg-accent-500" },
      { name: "Baja", count: 5, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 2, resolved: 1 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 2, resolved: 2 },
      { month: "Jun", created: 2, resolved: 2 },
      { month: "Jul", created: 1, resolved: 0 },
    ],
  },
  "ctr-auditoria": {
    totalTickets: 10, openTickets: 0, inProgressTickets: 0, resolvedTickets: 3, closedTickets: 7,
    slaCompliance: 99.2, avgResolutionHours: 2.2, avgFirstResponseMinutes: 4, satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Auditoría Interna", count: 4, color: "bg-accent-500" },
      { name: "Revisión Procesos", count: 3, color: "bg-primary-500" },
      { name: "Hallazgos", count: 2, color: "bg-amber-500" },
      { name: "Seguimiento", count: 1, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 1, color: "bg-red-500" },
      { name: "Alta", count: 4, color: "bg-amber-500" },
      { name: "Media", count: 4, color: "bg-accent-500" },
      { name: "Baja", count: 1, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 1, resolved: 1 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 1, resolved: 1 },
      { month: "Jun", created: 2, resolved: 2 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
};

// ═══════════════════════════════════════════════════════════
// SYSTEM STATS — DISEÑO Y MARKETING
// ═══════════════════════════════════════════════════════════

export const mockDisenoMarketingSystemStats: Record<string, ZammadTicketStats> = {
  "dm-digitales": {
    totalTickets: 34, openTickets: 0, inProgressTickets: 1, resolvedTickets: 9, closedTickets: 24,
    slaCompliance: 95.2, avgResolutionHours: 2.5, avgFirstResponseMinutes: 5, satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "BID Hoy", count: 10, color: "bg-accent-500" },
      { name: "Infografías", count: 8, color: "bg-primary-500" },
      { name: "Videos", count: 7, color: "bg-amber-500" },
      { name: "Presentaciones", count: 5, color: "bg-emerald-500" },
      { name: "Logotipos", count: 4, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 5, color: "bg-amber-500" },
      { name: "Media", count: 20, color: "bg-accent-500" },
      { name: "Baja", count: 9, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 3, resolved: 3 },
      { month: "Mar", created: 4, resolved: 4 },
      { month: "Abr", created: 3, resolved: 3 },
      { month: "May", created: 4, resolved: 4 },
      { month: "Jun", created: 5, resolved: 5 },
      { month: "Jul", created: 2, resolved: 1 },
    ],
  },
  "dm-impresos": {
    totalTickets: 20, openTickets: 1, inProgressTickets: 0, resolvedTickets: 4, closedTickets: 15,
    slaCompliance: 96.5, avgResolutionHours: 3.2, avgFirstResponseMinutes: 7, satisfactionAvg: 4.8,
    ticketsByCategory: [
      { name: "Flyers", count: 7, color: "bg-accent-500" },
      { name: "Trípticos", count: 5, color: "bg-primary-500" },
      { name: "Stands", count: 4, color: "bg-amber-500" },
      { name: "Art. Promocionales", count: 3, color: "bg-emerald-500" },
      { name: "One Pager", count: 1, color: "bg-secondary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 3, color: "bg-amber-500" },
      { name: "Media", count: 13, color: "bg-accent-500" },
      { name: "Baja", count: 4, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 2, resolved: 1 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 3, resolved: 3 },
      { month: "Jun", created: 2, resolved: 2 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "dm-audiovisual": {
    totalTickets: 8, openTickets: 0, inProgressTickets: 1, resolvedTickets: 2, closedTickets: 5,
    slaCompliance: 96.8, avgResolutionHours: 4.5, avgFirstResponseMinutes: 8, satisfactionAvg: 4.7,
    ticketsByCategory: [
      { name: "Producción Video", count: 3, color: "bg-accent-500" },
      { name: "Edición", count: 3, color: "bg-primary-500" },
      { name: "Animación", count: 1, color: "bg-amber-500" },
      { name: "Audio", count: 1, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 2, color: "bg-amber-500" },
      { name: "Media", count: 3, color: "bg-accent-500" },
      { name: "Baja", count: 3, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 1, resolved: 1 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 1, resolved: 1 },
      { month: "Jun", created: 2, resolved: 1 },
      { month: "Jul", created: 0, resolved: 1 },
    ],
  },
};

// ═══════════════════════════════════════════════════════════
// SYSTEM STATS — SERVICIOS GENERALES
// ═══════════════════════════════════════════════════════════

export const mockServiciosGeneralesSystemStats: Record<string, ZammadTicketStats> = {
  "sg-mantenimiento": {
    totalTickets: 38, openTickets: 2, inProgressTickets: 1, resolvedTickets: 10, closedTickets: 25,
    slaCompliance: 91.5, avgResolutionHours: 5.2, avgFirstResponseMinutes: 15, satisfactionAvg: 4.2,
    ticketsByCategory: [
      { name: "Correctivo", count: 14, color: "bg-accent-500" },
      { name: "Preventivo", count: 10, color: "bg-primary-500" },
      { name: "Emergencias", count: 8, color: "bg-amber-500" },
      { name: "Instalaciones", count: 6, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 2, color: "bg-red-500" },
      { name: "Alta", count: 11, color: "bg-amber-500" },
      { name: "Media", count: 18, color: "bg-accent-500" },
      { name: "Baja", count: 7, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 3, resolved: 3 },
      { month: "Mar", created: 5, resolved: 4 },
      { month: "Abr", created: 4, resolved: 4 },
      { month: "May", created: 5, resolved: 4 },
      { month: "Jun", created: 6, resolved: 5 },
      { month: "Jul", created: 2, resolved: 2 },
    ],
  },
  "sg-mensajeria": {
    totalTickets: 25, openTickets: 1, inProgressTickets: 0, resolvedTickets: 7, closedTickets: 17,
    slaCompliance: 93.8, avgResolutionHours: 3.5, avgFirstResponseMinutes: 10, satisfactionAvg: 4.5,
    ticketsByCategory: [
      { name: "Envío Local", count: 10, color: "bg-accent-500" },
      { name: "Envío Foráneo", count: 8, color: "bg-primary-500" },
      { name: "Paquetería", count: 5, color: "bg-amber-500" },
      { name: "Rastreo", count: 2, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 6, color: "bg-amber-500" },
      { name: "Media", count: 14, color: "bg-accent-500" },
      { name: "Baja", count: 5, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 3, resolved: 3 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 3, resolved: 3 },
      { month: "Jun", created: 4, resolved: 4 },
      { month: "Jul", created: 2, resolved: 1 },
    ],
  },
  "sg-papeleria": {
    totalTickets: 20, openTickets: 0, inProgressTickets: 1, resolvedTickets: 6, closedTickets: 13,
    slaCompliance: 94.2, avgResolutionHours: 4.8, avgFirstResponseMinutes: 12, satisfactionAvg: 4.3,
    ticketsByCategory: [
      { name: "Papelería Especial", count: 10, color: "bg-accent-500" },
      { name: "Material Oficina", count: 6, color: "bg-primary-500" },
      { name: "Pedidos Urgentes", count: 4, color: "bg-amber-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 5, color: "bg-amber-500" },
      { name: "Media", count: 10, color: "bg-accent-500" },
      { name: "Baja", count: 5, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 2, resolved: 2 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 2, resolved: 2 },
      { month: "Jun", created: 3, resolved: 3 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "sg-acceso": {
    totalTickets: 17, openTickets: 1, inProgressTickets: 0, resolvedTickets: 3, closedTickets: 13,
    slaCompliance: 93.1, avgResolutionHours: 2.5, avgFirstResponseMinutes: 6, satisfactionAvg: 4.6,
    ticketsByCategory: [
      { name: "Extravío Tarjeta", count: 7, color: "bg-accent-500" },
      { name: "Robo Tarjeta", count: 3, color: "bg-primary-500" },
      { name: "Reemplazo", count: 5, color: "bg-amber-500" },
      { name: "Acceso Temporal", count: 2, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 1, color: "bg-red-500" },
      { name: "Alta", count: 4, color: "bg-amber-500" },
      { name: "Media", count: 9, color: "bg-accent-500" },
      { name: "Baja", count: 3, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 1, resolved: 1 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 2, resolved: 1 },
      { month: "Jun", created: 2, resolved: 2 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "sg-proveedores": {
    totalTickets: 10, openTickets: 0, inProgressTickets: 1, resolvedTickets: 2, closedTickets: 7,
    slaCompliance: 95.8, avgResolutionHours: 3.1, avgFirstResponseMinutes: 8, satisfactionAvg: 4.7,
    ticketsByCategory: [
      { name: "Registro", count: 4, color: "bg-accent-500" },
      { name: "Autorización", count: 3, color: "bg-primary-500" },
      { name: "Vigencia", count: 2, color: "bg-amber-500" },
      { name: "Baja", count: 1, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 2, color: "bg-amber-500" },
      { name: "Media", count: 4, color: "bg-accent-500" },
      { name: "Baja", count: 4, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 1, resolved: 1 },
      { month: "Abr", created: 1, resolved: 0 },
      { month: "May", created: 1, resolved: 1 },
      { month: "Jun", created: 1, resolved: 2 },
      { month: "Jul", created: 1, resolved: 0 },
    ],
  },
};

// ═══════════════════════════════════════════════════════════
// SYSTEM STATS — TALENTO HUMANO
// ═══════════════════════════════════════════════════════════

export const mockTalentoHumanoSystemStats: Record<string, ZammadTicketStats> = {
  "th-reclutamiento": {
    totalTickets: 20, openTickets: 0, inProgressTickets: 0, resolvedTickets: 5, closedTickets: 15,
    slaCompliance: 97.5, avgResolutionHours: 2.5, avgFirstResponseMinutes: 5, satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Publicación Vacante", count: 6, color: "bg-accent-500" },
      { name: "Entrevistas", count: 5, color: "bg-primary-500" },
      { name: "Evaluación", count: 5, color: "bg-amber-500" },
      { name: "Onboarding", count: 4, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 4, color: "bg-amber-500" },
      { name: "Media", count: 11, color: "bg-accent-500" },
      { name: "Baja", count: 5, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 2, resolved: 2 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 3, resolved: 2 },
      { month: "Jun", created: 2, resolved: 2 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "th-nomina": {
    totalTickets: 25, openTickets: 0, inProgressTickets: 0, resolvedTickets: 6, closedTickets: 19,
    slaCompliance: 98.2, avgResolutionHours: 2.1, avgFirstResponseMinutes: 4, satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Incidencias", count: 8, color: "bg-accent-500" },
      { name: "Prestaciones", count: 6, color: "bg-primary-500" },
      { name: "Bonos", count: 6, color: "bg-amber-500" },
      { name: "CFDI", count: 5, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 3, color: "bg-amber-500" },
      { name: "Media", count: 15, color: "bg-accent-500" },
      { name: "Baja", count: 7, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 2, resolved: 2 },
      { month: "Mar", created: 3, resolved: 3 },
      { month: "Abr", created: 2, resolved: 2 },
      { month: "May", created: 3, resolved: 3 },
      { month: "Jun", created: 3, resolved: 3 },
      { month: "Jul", created: 2, resolved: 1 },
    ],
  },
  "th-capacitacion": {
    totalTickets: 16, openTickets: 1, inProgressTickets: 0, resolvedTickets: 4, closedTickets: 11,
    slaCompliance: 96.8, avgResolutionHours: 3.2, avgFirstResponseMinutes: 6, satisfactionAvg: 4.8,
    ticketsByCategory: [
      { name: "Inscripción Curso", count: 6, color: "bg-accent-500" },
      { name: "Certificaciones", count: 4, color: "bg-primary-500" },
      { name: "Material Didáctico", count: 3, color: "bg-amber-500" },
      { name: "Evaluación", count: 3, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 3, color: "bg-amber-500" },
      { name: "Media", count: 9, color: "bg-accent-500" },
      { name: "Baja", count: 4, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 2, resolved: 2 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 2, resolved: 2 },
      { month: "Jun", created: 2, resolved: 1 },
      { month: "Jul", created: 1, resolved: 1 },
    ],
  },
  "th-clima": {
    totalTickets: 8, openTickets: 0, inProgressTickets: 0, resolvedTickets: 2, closedTickets: 6,
    slaCompliance: 99.5, avgResolutionHours: 1.8, avgFirstResponseMinutes: 3, satisfactionAvg: 5.0,
    ticketsByCategory: [
      { name: "Encuestas", count: 3, color: "bg-accent-500" },
      { name: "Diagnóstico", count: 3, color: "bg-primary-500" },
      { name: "Plan Mejora", count: 2, color: "bg-amber-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 1, color: "bg-amber-500" },
      { name: "Media", count: 5, color: "bg-accent-500" },
      { name: "Baja", count: 2, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 1, resolved: 1 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 0, resolved: 0 },
      { month: "Jun", created: 2, resolved: 2 },
      { month: "Jul", created: 0, resolved: 0 },
    ],
  },
  "th-beneficios": {
    totalTickets: 10, openTickets: 0, inProgressTickets: 1, resolvedTickets: 2, closedTickets: 7,
    slaCompliance: 97.5, avgResolutionHours: 2.8, avgFirstResponseMinutes: 5, satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Seguro Médico", count: 4, color: "bg-accent-500" },
      { name: "Vales", count: 3, color: "bg-primary-500" },
      { name: "Fondo Ahorro", count: 2, color: "bg-amber-500" },
      { name: "Prestaciones Sup.", count: 1, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 2, color: "bg-amber-500" },
      { name: "Media", count: 5, color: "bg-accent-500" },
      { name: "Baja", count: 3, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 0 },
      { month: "Mar", created: 1, resolved: 2 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 1, resolved: 1 },
      { month: "Jun", created: 1, resolved: 1 },
      { month: "Jul", created: 1, resolved: 0 },
    ],
  },
  "th-expedientes": {
    totalTickets: 6, openTickets: 0, inProgressTickets: 0, resolvedTickets: 1, closedTickets: 5,
    slaCompliance: 99.8, avgResolutionHours: 1.5, avgFirstResponseMinutes: 3, satisfactionAvg: 5.0,
    ticketsByCategory: [
      { name: "Actualización", count: 3, color: "bg-accent-500" },
      { name: "Altas IMSS", count: 2, color: "bg-primary-500" },
      { name: "Bajas", count: 1, color: "bg-amber-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 1, color: "bg-amber-500" },
      { name: "Media", count: 3, color: "bg-accent-500" },
      { name: "Baja", count: 2, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 0, resolved: 0 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 0, resolved: 0 },
      { month: "Jun", created: 1, resolved: 1 },
      { month: "Jul", created: 0, resolved: 1 },
    ],
  },
};

// ═══════════════════════════════════════════════════════════
// SYSTEM STATS — LEGAL
// ═══════════════════════════════════════════════════════════

export const mockLegalSystemStats: Record<string, ZammadTicketStats> = {
  "leg-contratos": {
    totalTickets: 10, openTickets: 0, inProgressTickets: 1, resolvedTickets: 2, closedTickets: 7,
    slaCompliance: 98.2, avgResolutionHours: 2.4, avgFirstResponseMinutes: 5, satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Revisión", count: 4, color: "bg-accent-500" },
      { name: "Redacción", count: 3, color: "bg-primary-500" },
      { name: "Renovación", count: 2, color: "bg-amber-500" },
      { name: "Terminación", count: 1, color: "bg-emerald-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 2, color: "bg-amber-500" },
      { name: "Media", count: 6, color: "bg-accent-500" },
      { name: "Baja", count: 2, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 1, resolved: 1 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 2, resolved: 2 },
      { month: "Jun", created: 1, resolved: 1 },
      { month: "Jul", created: 1, resolved: 0 },
    ],
  },
  "leg-consultoria": {
    totalTickets: 8, openTickets: 0, inProgressTickets: 0, resolvedTickets: 3, closedTickets: 5,
    slaCompliance: 99.1, avgResolutionHours: 2.2, avgFirstResponseMinutes: 4, satisfactionAvg: 5.0,
    ticketsByCategory: [
      { name: "Dictámenes", count: 3, color: "bg-accent-500" },
      { name: "Opiniones", count: 3, color: "bg-primary-500" },
      { name: "Análisis Riesgo", count: 2, color: "bg-amber-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 2, color: "bg-amber-500" },
      { name: "Media", count: 4, color: "bg-accent-500" },
      { name: "Baja", count: 2, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 1, resolved: 1 },
      { month: "Mar", created: 1, resolved: 0 },
      { month: "Abr", created: 0, resolved: 0 },
      { month: "May", created: 1, resolved: 1 },
      { month: "Jun", created: 1, resolved: 2 },
      { month: "Jul", created: 0, resolved: 0 },
    ],
  },
  "leg-gobierno": {
    totalTickets: 5, openTickets: 0, inProgressTickets: 0, resolvedTickets: 1, closedTickets: 4,
    slaCompliance: 98.5, avgResolutionHours: 1.8, avgFirstResponseMinutes: 4, satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Actas Asamblea", count: 2, color: "bg-accent-500" },
      { name: "Poderes", count: 2, color: "bg-primary-500" },
      { name: "Régimen Societario", count: 1, color: "bg-amber-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 1, color: "bg-amber-500" },
      { name: "Media", count: 3, color: "bg-accent-500" },
      { name: "Baja", count: 1, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 0, resolved: 0 },
      { month: "Mar", created: 1, resolved: 1 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 0, resolved: 0 },
      { month: "Jun", created: 1, resolved: 1 },
      { month: "Jul", created: 0, resolved: 0 },
    ],
  },
  "leg-litigio": {
    totalTickets: 2, openTickets: 0, inProgressTickets: 0, resolvedTickets: 0, closedTickets: 2,
    slaCompliance: 100, avgResolutionHours: 5.2, avgFirstResponseMinutes: 3, satisfactionAvg: 4.8,
    ticketsByCategory: [
      { name: "Juicios", count: 1, color: "bg-accent-500" },
      { name: "Arbitraje", count: 1, color: "bg-primary-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 0, color: "bg-amber-500" },
      { name: "Media", count: 2, color: "bg-accent-500" },
      { name: "Baja", count: 0, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 0, resolved: 1 },
      { month: "Mar", created: 1, resolved: 1 },
      { month: "Abr", created: 0, resolved: 0 },
      { month: "May", created: 0, resolved: 0 },
      { month: "Jun", created: 0, resolved: 0 },
      { month: "Jul", created: 1, resolved: 0 },
    ],
  },
  "leg-propiedad": {
    totalTickets: 3, openTickets: 0, inProgressTickets: 0, resolvedTickets: 1, closedTickets: 2,
    slaCompliance: 99.5, avgResolutionHours: 3.5, avgFirstResponseMinutes: 6, satisfactionAvg: 4.9,
    ticketsByCategory: [
      { name: "Marcas", count: 1, color: "bg-accent-500" },
      { name: "Patentes", count: 1, color: "bg-primary-500" },
      { name: "Derechos Autor", count: 1, color: "bg-amber-500" },
    ],
    ticketsByPriority: [
      { name: "Crítica", count: 0, color: "bg-red-500" },
      { name: "Alta", count: 0, color: "bg-amber-500" },
      { name: "Media", count: 2, color: "bg-accent-500" },
      { name: "Baja", count: 1, color: "bg-foreground-400" },
    ],
    monthlyTrend: [
      { month: "Feb", created: 0, resolved: 0 },
      { month: "Mar", created: 0, resolved: 0 },
      { month: "Abr", created: 1, resolved: 1 },
      { month: "May", created: 0, resolved: 0 },
      { month: "Jun", created: 1, resolved: 1 },
      { month: "Jul", created: 0, resolved: 0 },
    ],
  },
};

// ═══════════════════════════════════════════════════════════
// MASTER SYSTEM STATS LOOKUP (all systems, all depts)
// ═══════════════════════════════════════════════════════════

export const allSystemStats: Record<string, ZammadTicketStats> = {
  ...mockTISystemStats,
  ...mockAdministracionSystemStats,
  ...mockBidTecnologiaSystemStats,
  ...mockContraloriaSystemStats,
  ...mockDisenoMarketingSystemStats,
  ...mockServiciosGeneralesSystemStats,
  ...mockTalentoHumanoSystemStats,
  ...mockLegalSystemStats,
};
